import { v4 as uuidv4 } from "uuid";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { checkPromptSafety } from "./shared/check-prompt-safety";
import { fetchInferenceJob } from "./shared/fetch-inference-job";
import { getTargetEditorImage } from "./shared/target-editor-image";

const callNanoBananaEditing = async (
	prompt: string,
	resolution: string,
	model: string,
	input_images: string[],
	messageId: string,
	jwt: string,
	tool_document_id: string,
	batch_size: number,
) => {
	const promptSafetyResult = checkPromptSafety(prompt);
	if (promptSafetyResult.detected) {
		await promptSafetyResult.onFailed();
		return false;
	}

	const { success } = await getTargetEditorImage({
		commandName: "Nano Banana - Image Editing",
		resultLayerName: "Nano Banana - Image Editing",
		includeMask: true,
		applyMaskToResultLayer: true,
		placeAsSmartObject: true,
		maxSide: undefined,
		processImage: async ({
			executionContext,
			imageBase64,
		}) => {
			const data = {
				prompt,
				resolution,
				model,
				input_images: [imageBase64, ...input_images],
				from_editor: true,
				jwt,
				tool_document_id,
				batch_size,
			};

			return fetchNanoBananaEditing(data, executionContext, messageId);
		},
	});

	if (success) {
		const doc = photoshop.app.activeDocument;
		if (doc) {
			postToRenderer([
				{
					key: "documentInfoChanged",
					id: uuidv4(),
					values: {
						width: doc.width,
						height: doc.height,
					},
				},
			]);
		}
	}

	return success;
};

export async function fetchNanoBananaEditing(
	params: {
		prompt: string;
		input_images: string[];
		resolution: string;
		model: string;
		jwt: string;
		tool_document_id: string;
		batch_size: number;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/gemini",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/gemini", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/gemini", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/gemini", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenNanoBananaEditingMessage() {
	useListenFromRenderer([
		{
			key: "nanoBananaEditing",
			onMessage: async (message) => {
				const success = await callNanoBananaEditing(
					message.values.prompt,
					message.values.resolution,
					message.values.model,
					message.values.input_images,
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
					message.values.batch_size,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
